import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { api, ApiRequestError } from '../lib/api';
import { PageHeader } from '../components/shell/PageHeader';
import { SettingsTabs, type SettingsTab } from '../components/settings/SettingsTabs';
import { useFooterSave } from '../components/settings/footerSave';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { FormField, Input } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { CHANGES_SAVED, useToast } from '../components/ui/toast';
import { MediaGeneratorsCard } from '../features/media/MediaGeneratorsCard';
import { useIsTenantAdmin } from '../auth/useWorkspaceAdmin';
import { usePlatformSettingsSave, SaveRow } from '../features/settings/settingsSave';
import { KeyStatus, MASKED } from '../features/settings/integrationUi';
import { FigmaCard } from '../features/settings/FigmaCard';
import { CanvaCard } from '../features/settings/CanvaCard';
import { SocialMediaIntegrationsCard } from '../features/settings/SocialMediaIntegrationsCard';
import { describeSiteAudit } from '../features/settings/siteAudit';
import type { SiteIntegrationsView, UpdatePlatformSettingsDto, WebhookTestResult } from '@mg/shared';

const WEBHOOK_HINT =
  'Your events (content scheduled, content published, source pulled) are POSTed here as JSON with a top-level "type" field so one universal webhook can route them. Overrides the system default.';
const APIFY_HINT =
  'Kept on our server (write-only — never shown again) and used to run this install’s Apify-backed Digest Sources.';
const APIFY_USER_ID_HINT =
  'Optional label for your own reference (not a secret). The account verified from the key is shown above.';

/**
 * The page's sections as tabs, in page order (spec "App Settings, Billing Admin and Site
 * Integrations"). Every visitor who gets past the role check is a Tenant Admin, so each tab with
 * fields is one they can save: Webhook URL, Apify, Figma and Canva save through the sticky Save
 * footer. Social Media Integrations and Media Generators keep the Save inside each row's form, and
 * Recent site changes has nothing to save. Labels are shorter than some titles; each panel keeps its
 * card's full title.
 */
const TABS: SettingsTab[] = [
  { key: 'social', label: 'Social Media Integrations', render: () => <SocialMediaIntegrationsCard /> },
  {
    key: 'webhook',
    label: 'Webhook URL',
    footer: true,
    render: () => (
      <WithSiteIntegrations>{(site) => <WebhookCard initialUrl={site.webhookUrl ?? ''} />}</WithSiteIntegrations>
    ),
  },
  {
    key: 'apify',
    label: 'Apify',
    footer: true,
    render: () => (
      <WithSiteIntegrations>
        {(site) => <ApifyCard keySet={site.apifyKeySet} apifyUserId={site.apifyUserId} />}
      </WithSiteIntegrations>
    ),
  },
  { key: 'media', label: 'Media Generators', render: () => <MediaGeneratorsCard /> },
  {
    key: 'figma',
    label: 'Figma',
    footer: true,
    render: () => <WithSiteIntegrations>{(site) => <FigmaCard patSet={site.figmaPatSet} />}</WithSiteIntegrations>,
  },
  {
    key: 'canva',
    label: 'Canva',
    footer: true,
    render: () => (
      <WithSiteIntegrations>
        {(site) => (
          <CanvaCard
            clientId={site.canvaClientId}
            clientSecretSet={site.canvaClientSecretSet}
            connected={site.canvaConnected}
            connectedUser={site.canvaConnectedUser}
            canvaRedirectUri={site.canvaRedirectUri}
          />
        )}
      </WithSiteIntegrations>
    ),
  },
  { key: 'changes', label: 'Recent site changes', render: () => <SiteAuditCard /> },
];

/**
 * Corporate/site-wide integrations, moved off App Settings into their own
 * Tenant-Admin-only surface (reachable from the profile dropdown). Each card reads +
 * writes the SITE-wide integration config (PlatformSettings) via
 * `api.getSiteIntegrations` / `api.updatePlatformSettings` — secrets are stored
 * write-only and surfaced only as set/not-set indicators. The Social Media Integrations card has
 * its own endpoints (site-integrations/social-app-keys) and its own loading state.
 *
 * Each section is a tab (TABS), and only the selected tab is drawn, so only it loads. The selected tab
 * is kept in `?tab=`. Canva's consent screen sends the admin back here with `?canva=connected` or
 * `?canva=failed`: that opens the Canva tab, whose card reports how the connection went and replaces
 * the parameter with `?tab=canva` (the tab stays open, and a reload keeps it).
 */
export function SiteIntegrationsPage() {
  // Site Integrations are install-level corporate config: Tenant Admins only. The
  // dropdown link is hidden for everyone else, but self-gate here against direct URL
  // access so we never render (or fire the queries behind) the integration cards.
  const isTenantAdmin = useIsTenantAdmin();
  const [searchParams] = useSearchParams();

  if (!isTenantAdmin) {
    return (
      <>
        <PageHeader title="Site Integrations" />
        <Card>
          <CardBody>
            <p className="text-sm text-ink-muted">
              You don&apos;t have access to Site Integrations. Ask a Tenant Admin.
            </p>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Site Integrations"
        description="Connect the corporate integrations for your install."
      />
      <SettingsTabs
        label="Site Integrations sections"
        tabs={TABS}
        initialTab={searchParams.has('canva') ? 'canva' : undefined}
      />
    </>
  );
}

/**
 * The masked site-integrations config that the Webhook URL, Apify, Figma and Canva tabs edit: a
 * loading or error card until it is in, then the tab's card with it. Each of those tabs mounts its own
 * (one query, shared through the cache), so the tabs that do not use it (Social Media Integrations,
 * Media Generators, Recent site changes) never wait for it or fail with it. Mounted only for Tenant
 * Admins (the page self-gates), so the Tenant-Admin-only query never fires for anyone else.
 */
function WithSiteIntegrations({ children }: { children: (site: SiteIntegrationsView) => ReactNode }) {
  const siteQuery = useQuery({
    queryKey: ['site-integrations'],
    queryFn: ({ signal }) => api.getSiteIntegrations(signal),
  });

  if (siteQuery.isLoading) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-ink-muted">Loading integrations…</p>
        </CardBody>
      </Card>
    );
  }

  if (siteQuery.isError || !siteQuery.data) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load your site integrations.</p>
        </CardBody>
      </Card>
    );
  }

  return <>{children(siteQuery.data)}</>;
}

/** Format an ISO timestamp for the audit list (locale date + time). */
function formatAuditTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/**
 * Read-only log of recent install-level (site-wide) changes — settings/integration edits,
 * media-credential saves/removals, and Tenant-Admin grants/revokes. Tenant-Admin only (the
 * whole page is), and never shows secret values (the API records keys as set/cleared).
 *
 * It has its own tab, so it no longer folds away: the list loads when the tab opens.
 */
function SiteAuditCard() {
  const auditQuery = useQuery({
    queryKey: ['site-audit'],
    queryFn: ({ signal }) => api.getSiteAudit(signal),
  });

  return (
    <Card>
      <CardHeader
        title="Recent site changes"
        description="Site-wide setting (including AI models), integration, media-credential, and Tenant-Admin changes. Never shows secret values."
      />
      <CardBody>
        {auditQuery.isLoading ? (
          <p className="text-sm text-ink-faint">Loading recent changes…</p>
        ) : auditQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load recent changes.</p>
        ) : (auditQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-ink-faint">No site changes recorded yet.</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {(auditQuery.data ?? []).map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm text-ink">{describeSiteAudit(entry)}</p>
                  <p className="text-xs text-ink-faint">by {entry.actorName ?? 'Unknown'}</p>
                </div>
                <span className="shrink-0 text-xs text-ink-faint">
                  {formatAuditTime(entry.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function WebhookCard({ initialUrl }: { initialUrl: string }) {
  const [url, setUrl] = useState(initialUrl);
  const { saving, saved, setSaved, error, save } = usePlatformSettingsSave();
  const toast = useToast();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);

  const dirty = url !== initialUrl;
  const hasSavedUrl = initialUrl.trim() !== '';

  // Sends the URL trimmed and, once that worked, shows it trimmed: white space at either end (a pasted
  // no-break space, which the field keeps) would otherwise leave it "changed" against the stored URL
  // straight after the save, with the footer still asking to save it.
  const saveUrl = async (): Promise<boolean> => {
    const typed = url;
    const ok = await save({ webhookUrl: typed.trim() });
    if (ok) setUrl((current) => (current === typed ? typed.trim() : current));
    return ok;
  };

  const { inFooter } = useFooterSave({
    id: 'webhook',
    dirty,
    saving,
    save: saveUrl,
    discard: () => setUrl(initialUrl),
  });

  // The card's own Save, drawn only where no footer saves it.
  const onSave = async () => {
    if (await saveUrl()) toast.show(CHANGES_SAVED);
  };

  const onTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await api.testWebhook());
    } catch (err) {
      setTestResult({
        delivered: false,
        status: null,
        url: initialUrl,
        error: err instanceof ApiRequestError ? err.message : 'Test failed.',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Webhook URL for Smorgas Integration"
        description="Send this install's Smorgas Social events to an external n8n / automation workflow."
      />
      <CardBody className="space-y-4">
        <FormField label="Webhook URL" htmlFor="integration-webhook-url" hint={WEBHOOK_HINT}>
          <Input
            id="integration-webhook-url"
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => {
              setSaved(false);
              setTestResult(null);
              setUrl(e.target.value);
            }}
            placeholder="https://myapp.example.com/webhook/endpoint"
          />
        </FormField>

        <div className="flex flex-wrap items-center gap-3">
          {!inFooter && (
            <Button onClick={() => void onSave()} loading={saving} disabled={saving || !dirty}>
              Save
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => void onTest()}
            loading={testing}
            disabled={testing || dirty || !hasSavedUrl}
            title={dirty ? 'Save the URL first, then send a test' : undefined}
          >
            Send test
          </Button>
          {saved && !dirty && (
            <span className="flex items-center gap-1 text-sm text-success dark:text-success-dark">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
        </div>

        {dirty && url.trim() !== '' && (
          <p className="text-xs text-ink-faint">Save to test the latest URL.</p>
        )}

        {testResult && (
          <p className={testResult.delivered ? 'text-sm text-success dark:text-success-dark' : 'text-sm text-red-600 dark:text-error-dark'}>
            {testResult.delivered
              ? `✓ Delivered — your webhook responded HTTP ${testResult.status}.`
              : `✗ Not delivered${testResult.status ? ` — HTTP ${testResult.status}` : ''}${
                  testResult.error ? `: ${testResult.error}` : '.'
                }`}
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function ApifyCard({ keySet, apifyUserId }: { keySet: boolean; apifyUserId: string | null }) {
  const [userId, setUserId] = useState(apifyUserId ?? '');
  // The key is write-only. When one is stored the field shows the MASKED mask (see MASKED);
  // focusing it clears the mask so a new key can be typed. `keyTouched` distinguishes "the admin
  // is entering a new key" from "leave the stored key alone".
  const [apiKey, setApiKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const { saving, saved, setSaved, error, save } = usePlatformSettingsSave();
  const toast = useToast();

  // Live lookup of the account behind the configured key, so the admin can tell WHICH Apify
  // account is active (the masked key can't reveal that). Invalidated on save (see settingsSave).
  const identityQuery = useQuery({
    queryKey: ['apify-identity'],
    queryFn: ({ signal }) => api.getApifyIdentity(signal),
  });
  const identity = identityQuery.data;

  const keyDisplay = keyTouched ? apiKey : keySet ? MASKED : '';
  const userIdChanged = userId.trim() !== (apifyUserId ?? '').trim();
  const keyChanged = keyTouched && apiKey.trim() !== '';
  const dirty = userIdChanged || keyChanged;

  // Re-mask the key field once a save lands (the "Key set" badge / masked value reflect the result).
  useEffect(() => {
    if (saved) {
      setApiKey('');
      setKeyTouched(false);
    }
  }, [saved]);

  // Only what changed: the key only when one was typed, so the stored key is never re-sent or cleared.
  const patch = (): UpdatePlatformSettingsDto => ({
    ...(userIdChanged ? { apifyUserId: userId.trim() } : {}),
    ...(keyChanged ? { apifyApiKey: apiKey.trim() } : {}),
  });

  const { inFooter } = useFooterSave({
    id: 'apify',
    dirty,
    saving,
    save: () => save(patch()),
    // Back to the saved User ID and the masked key.
    discard: () => {
      setUserId(apifyUserId ?? '');
      setApiKey('');
      setKeyTouched(false);
    },
  });

  // The card's own Save, drawn only where no footer saves it.
  const onSave = () =>
    void save(patch()).then((ok) => {
      if (ok) toast.show(CHANGES_SAVED);
    });

  return (
    <Card>
      <CardHeader
        title="Apify Integration"
        description="API key + account used to run this install's Apify-backed Digest Sources."
      />
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-strong">Status</span>
          <KeyStatus set={keySet} />
        </div>

        {/* Live-verified account behind the configured key — the reliable way to tell which of
            several Apify accounts is active. */}
        <div className="rounded-lg border border-line bg-page/60 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium text-ink-label">Configured Apify account</span>
            <button
              type="button"
              onClick={() => void identityQuery.refetch()}
              disabled={identityQuery.isFetching}
              className="focus-ring text-xs font-medium text-link hover:text-action-hover hover:underline disabled:opacity-50"
            >
              {identityQuery.isFetching ? 'Checking…' : 'Re-check'}
            </button>
          </div>
          <div className="mt-1 text-sm">
            {identityQuery.isLoading ? (
              <span className="text-ink-faint">Checking…</span>
            ) : identityQuery.isError ? (
              <span className="text-red-600 dark:text-error-dark">Could not check the configured account.</span>
            ) : !identity?.configured ? (
              <span className="text-ink-faint">No Apify key configured.</span>
            ) : identity.error ? (
              <span className="text-amber-700 dark:text-warning-dark">Key set, but couldn’t verify: {identity.error}</span>
            ) : (
              <span className="text-ink">
                <span className="font-medium">{identity.username ?? 'Apify account'}</span>
                {identity.id && <span className="text-ink-muted"> · id {identity.id}</span>}
                {identity.source === 'env' && (
                  // No SITE key is stored (the Status badge reads "No key set") — this account
                  // comes from the deployment's default APIFY_TOKEN. Label it so the two agree.
                  <span className="text-ink-faint"> · via server default key</span>
                )}
              </span>
            )}
          </div>
        </div>

        <FormField
          label={keySet ? 'Replace Apify API key' : 'Apify API key'}
          htmlFor="apify-api-key"
          hint={APIFY_HINT}
        >
          <Input
            id="apify-api-key"
            type="password"
            autoComplete="off"
            value={keyDisplay}
            onFocus={() => {
              // First focus on a masked field clears the mask so the admin types a fresh key.
              if (!keyTouched) {
                setKeyTouched(true);
                setApiKey('');
              }
            }}
            onChange={(e) => {
              setSaved(false);
              setKeyTouched(true);
              setApiKey(e.target.value);
            }}
            onBlur={() => {
              // Left untouched-empty → restore the mask (means "keep the stored key").
              if (keyTouched && apiKey === '') setKeyTouched(false);
            }}
            placeholder={keySet ? MASKED : 'apify_api_…'}
          />
        </FormField>

        <FormField label="Apify User ID" htmlFor="apify-user-id" hint={APIFY_USER_ID_HINT}>
          <Input
            id="apify-user-id"
            type="text"
            autoComplete="off"
            value={userId}
            onChange={(e) => {
              setSaved(false);
              setUserId(e.target.value);
            }}
            placeholder="e.g. aBcD1234efGh"
          />
        </FormField>

        <SaveRow inFooter={inFooter} saving={saving} saved={saved} dirty={dirty} error={error} onSave={onSave} />
      </CardBody>
    </Card>
  );
}
